import { UIPoint, UITextView, UIView } from "uicore-ts"
import { RHColors, RHRadius, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHCostSummaryViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { RHEuroFormatter } from "../Custom components/RH/Utils_slop/RHEuroFormatter"
import type { RHCPVValue } from "../Custom components/RH/Utils_slop/RHCPVCodeFormatter"

interface CostEntry {
    cpvCode: string
    cost: number
}

export class CostSummaryView extends UIView {
    
    readonly totalLabel = new UITextView()
        .addedAsSubviewToView(this)
    
    readonly breakdownLabel = new UITextView()
        .addedAsSubviewToView(this)
    
    constructor(elementID?: string) {
        
        super(elementID)
        
        ;(this as CostSummaryView).configureWithObject({
            style: {
                borderRadius: `${RHRadius.large}px`,
                background: RHColors.blackAlpha04.stringValue
            }
        })
        this.totalLabel.configureWithObject({
            style: {
                fontWeight: "600",
                lineHeight: "1.3"
            }
        })
        this.breakdownLabel.configureWithObject({
            style: {
                opacity: "0.7",
                fontSize: "13px"
            }
        })
        this.breakdownLabel.hidden = true
        
    }
    
    update(entries: CostEntry[]) {
        const validEntries = entries.filter(e => e.cost > 0)
        
        if (validEntries.length === 0) {
            this.totalLabel.text = "Eeldatav kogumaksumus (KM-ta): —"
            this.breakdownLabel.text = ""
            this.breakdownLabel.hidden = true
            this.setNeedsLayout()
            return
        }
        
        const total = validEntries.reduce((sum, e) => sum + e.cost, 0)
        this.totalLabel.text = `Eeldatav kogumaksumus (KM-ta): ${this._formatCurrency(total)}`
        
        if (validEntries.length > 1) {
            const byCPV: Record<string, number> = {}
            for (const entry of validEntries) {
                const key = entry.cpvCode || "Täpsustamata"
                byCPV[key] = (byCPV[key] ?? 0) + entry.cost
            }
            this.breakdownLabel.text = Object.entries(byCPV)
                .map(([cpv, cost]) => `${cpv}: ${this._formatCurrency(cost)}`)
                .join("  ·  ")
            this.breakdownLabel.hidden = false
        }
        else {
            this.breakdownLabel.text = ""
            this.breakdownLabel.hidden = true
        }
        
        this.setNeedsLayout()
        
    }
    
    updateFromProcurementParts(parts: Array<{ CPV?: RHCPVValue; estimatedCost?: number | string | null }>) {
        this.update(parts.map(p => ({
            cpvCode: (p.CPV?.toString() ?? "").split(" ").shift() ?? "",
            cost: Number(p.estimatedCost) || 0
        })))
    }
    
    private _formatCurrency(value: number): string {
        return RHEuroFormatter.formattedAmount(value)
    }
    
    override intrinsicContentWidth(_constrainingHeight = 0): number {
        const horizontalPadding = RHSpacing.small
        const totalWidth = this.totalLabel.intrinsicContentWidth()
        const breakdownWidth = this.breakdownLabel.text
            ? this.breakdownLabel.intrinsicContentWidth()
            : 0
        
        return Math.max(totalWidth, breakdownWidth) + horizontalPadding * 2
    }
    
    override intrinsicContentHeight(constrainingWidth = 0): number {
        const horizontalPadding = RHSpacing.small
        const verticalPadding = RHSpacing.tiny
        const availableWidth = Math.max(0, constrainingWidth - horizontalPadding * 2)
        const totalHeight = this.totalLabel.intrinsicContentHeight(availableWidth)
        const breakdownHeight = this.breakdownLabel.text
            ? RHCostSummaryViewTokens.breakdownTopGap +
                this.breakdownLabel.intrinsicContentHeight(availableWidth)
            : 0
        
        return verticalPadding * 2 + totalHeight + breakdownHeight
    }
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const horizontalPadding = RHSpacing.small
        const verticalPadding = RHSpacing.tiny
        const bounds = this.contentBoundsWithInsets(
            verticalPadding,
            horizontalPadding,
            verticalPadding,
            horizontalPadding
        )
        
        if (this.breakdownLabel.text) {
            bounds
                .rectangleWithHeight(this.totalLabel)
                .assignedAsFrameOfView(this.totalLabel)
                .rectangleForNextRow(RHCostSummaryViewTokens.breakdownTopGap, this.breakdownLabel)
                .assignedAsFrameOfView(this.breakdownLabel)
        }
        else {
            bounds
                .rectangleWithHeight(
                    this.totalLabel,
                    0.5
                )
                .offsetByPoint(new UIPoint(0, (RHSpacing.tiny * -0.5)))
                .assignedAsFrameOfView(this.totalLabel)
            this.breakdownLabel.frame = bounds.rectangleWithHeight(0)
        }
        
    }
    
}
